// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { URLLink } from '@/url-link/api/types';
import { dateTimeSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

export type FileContext =
  | 'LOGO'
  | 'CUSTOMER_LOGO'
  | 'ICON'
  | 'MISC'
  | 'ASSET_FILE'
  | 'TICKET_FILE'
  | 'PROJECT_FILE'
  | 'FULFILLMENT_ORDER_FILE'
  | 'RETURN_ORDER_FILE'
  | 'PURCHASE_ORDER_FILE';

/**
 * Returns a URL for a file with the given key, or null if a URL cannot be
 * created.
 *
 * @param key - The key of the file.
 */
function getFileURL(key: string) {
  try {
    const baseURL = new URL('/v1/file/', import.meta.env.VITE_API_URL);

    return new URL(key, baseURL);
  } catch (error) {
    Sentry.captureException(error);
  }

  return null;
}

export const fileSchema = z
  .object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
    type: z.string().optional(),
    mimeType: z.string().optional(),
    size: z.number().optional(),
    uploadedAt: dateTimeSchema,
  })
  .transform((file) => {
    const url = getFileURL(file.key);

    if (url === null) {
      return z.NEVER;
    }

    return { url, ...file };
  });

export type File = z.infer<typeof fileSchema>;

/**
 * Unified attachment schema that represents both files and URL links.
 * This allows for a single table interface to display both types of attachments.
 */
export const attachmentSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string().nullable(),
  type: z.enum(['file', 'link']),
  url: z.string().url(),
  size: z.number().optional(),
  mimeType: z.string().optional(),
  datedAt: dateTimeSchema,
});

export type Attachment = z.infer<typeof attachmentSchema>;

/**
 * Converts a File to an Attachment type for unified display.
 */
export function fileToAttachment(file: File): Attachment {
  return {
    id: file.id,
    key: file.key,
    name: file.name,
    type: 'file' as const,
    url: file.url.toString(),
    size: file.size,
    mimeType: file.mimeType,
    datedAt: file.uploadedAt,
  };
}

/**
 * Converts a URLLink to an Attachment type for unified display.
 */
export function urlLinkToAttachment(urlLink: URLLink): Attachment {
  return {
    id: urlLink.id,
    key: urlLink.key,
    name: urlLink.name,
    type: 'link' as const,
    url: urlLink.url,
    size: undefined,
    datedAt: urlLink.updatedAt,
  };
}

/**
 * Combines files and URL links into a single array of attachments for unified display.
 * Sorts by creation date in descending order (newest first).
 */
export function combineAsAttachments(
  files: File[],
  urlLinks: URLLink[]
): Attachment[] {
  const combined: Attachment[] = [
    ...files.map(fileToAttachment),
    ...urlLinks.map(urlLinkToAttachment),
  ];

  return combined.sort((a, b) => b.datedAt.toMillis() - a.datedAt.toMillis());
}
